/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.dev/license
 */

import { NgOptimizedImage } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  ViewEncapsulation,
} from '@angular/core';

/**  Quality of the placeholder image.  */
export type PlaceholderImageQuality =
  'high' | 'standard' | 'low' | 'medium' | 'max';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector -- vendored fork of @angular/youtube-player; keeps upstream's real tag name rather than an app- prefix, see youtube-player.ts's file header
  selector: 'youtube-player-placeholder',
  imports: [NgOptimizedImage],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `
    <img
      [ngSrc]="_getBackgroundImage()"
      fill
      alt=""
      [priority]="placeholderPriority()"
    />
    <button
      type="button"
      class="youtube-player-placeholder-button"
      [attr.aria-label]="buttonLabel()"
    >
      <img
        src="/social-icons/youtube-play-button.svg"
        alt=""
        width="68"
        height="48"
      />
    </button>
  `,
  styleUrl: 'youtube-player-placeholder.scss',
  host: {
    class: 'youtube-player-placeholder',
    '[class.youtube-player-placeholder-loading]': 'isLoading()',
    '[style.width.px]': 'width()',
    '[style.height.px]': 'height()',
    '[style.position]': "'relative'",
  },
})
export class YouTubePlayerPlaceholder {
  /** ID of the video for which to show the placeholder. */
  videoId = input.required<string>();

  /** Width of the video for which to show the placeholder. */
  width = input.required<number>();

  /** Height of the video for which to show the placeholder. */
  height = input.required<number>();

  /** Whether the video is currently being loaded. */
  isLoading = input.required<boolean>();

  /** Accessible label for the play button. */
  buttonLabel = input.required<string>();

  /** Quality of the placeholder image. */
  quality = input.required<PlaceholderImageQuality>();

  // custom inputs
  webpImage = input(true);
  placeholderPriority = input(false, { transform: booleanAttribute });

  /** Gets the background image showing the placeholder. */
  protected _getBackgroundImage = computed((): string => {
    let fileQuality;

    if (this.quality() === 'high') {
      fileQuality = 'hqdefault';
    } else if (this.quality() === 'medium') {
      fileQuality = 'mqdefault';
    } else if (this.quality() === 'max') {
      fileQuality = 'maxresdefault';
    } else {
      fileQuality = 'sddefault';
    }

    return `https://i.ytimg.com/vi${this.webpImage() ? '_webp' : ''}/${this.videoId()}/${fileQuality}.${this.webpImage() ? 'webp' : 'jpg'}`;
  });
}
